import { z } from "zod";
import { AgentId, AgentVersionId, GameId, GameKind, PaginationQuery } from "../../../common";
import { AiPurpose } from "../../shared/calls";
import { CostLine } from "../../shared/pricing";

export const AiCallId = z.uuid().meta({
  id: "AiCallId",
  description: "The id of one vendor request Pax made for a model call.",
  example: "0199f0c4-1d2e-7f3a-8b4c-5d6e7f8a9b0c",
});

export const AiCallParams = z.strictObject({ callId: AiCallId });
export type AiCallParams = z.infer<typeof AiCallParams>;

export const ListAiCallsQuery = PaginationQuery.extend({
  gameId: GameId.optional(),
  agentId: AgentId.optional(),
  agentVersionId: AgentVersionId.optional(),
  purpose: AiPurpose.optional(),
});
export type ListAiCallsQuery = z.infer<typeof ListAiCallsQuery>;

const Nullable = z.string().nullable();

/** Where a call went: what the request asked for, or what the response reported. */
const AiCallRoute = z.object({
  model: Nullable,
  /** Whose servers a router sent the call to, such as `amazon-bedrock`; `auto` when it chose. */
  upstream: Nullable,
  /** The service tier; `auto` when the seller chose. */
  tier: Nullable,
  inferenceLocation: Nullable,
  dataLocation: Nullable,
});

/** One vendor request Pax made for a model call, as `model_calls` stores it, without its bodies. */
export const AiCall = z
  .object({
    callId: AiCallId,
    /** The ledger hold the call was placed under, or null when nothing was reserved. */
    callAttemptId: Nullable,
    traceId: z.string(),
    spanId: z.string(),
    parentSpanId: Nullable,
    purpose: AiPurpose,
    /** Null, with every other game and agent field, for a signed-in user's own call. */
    gameId: z.uuid().nullable(),
    gameKind: GameKind.nullable(),
    gameRound: z.number().int().nullable(),
    gameHeadRevision: z.number().int().nullable(),
    agentId: z.uuid().nullable(),
    agentVersionId: z.uuid().nullable(),
    /** The running copy of that version in the game, as the game server names it. */
    agentInstanceId: z.uuid().nullable(),
    /** The context window the agent made the call in, as the agent names it. */
    conversationId: Nullable,
    conversationCompactions: z.number().int().nullable(),
    /** Who bills Pax for the call, such as `anthropic` or `openrouter`. */
    billedBy: z.string(),
    /** The vendor endpoint the call came over, such as `anthropic-messages`. */
    transport: z.string(),
    credential: z.enum(["pax", "player"]),
    /** What the request asked for. */
    requested: AiCallRoute.extend({ model: z.string() }),
    /** What the response reported; a null fact was not reported. */
    served: AiCallRoute,
    /** The SKU the request named, which prediction and cost are priced at; null when Pax has none. */
    sku: Nullable,
    /** The model as its lab names it, such as `claude-haiku-4-5`. */
    model: Nullable,
    modelCreator: Nullable,
    upstreamResponseId: Nullable,
    stream: z.boolean(),
    maxOutputTokens: z.number().int().nullable(),
    temperature: z.number().nullable(),
    topP: z.number().nullable(),
    outputFormat: Nullable,
    promptCacheKey: Nullable,
    /** When the API received the call. */
    receivedAt: z.string(),
    endedAt: z.string(),
    /** Each moment in milliseconds after `receivedAt`; null when it didn't happen. */
    timing: z.object({
      forwardedMs: z.number().int().nullable(),
      firstByteMs: z.number().int().nullable(),
      firstEventMs: z.number().int().nullable(),
      firstReasoningMs: z.number().int().nullable(),
      firstOutputMs: z.number().int().nullable(),
      lastOutputMs: z.number().int().nullable(),
    }),
    /** `response`, or `none` when the response reported no usage. */
    usageSource: z.enum(["response", "none"]),
    inputTokens: z.number().int(),
    implicitCacheReadTokens: z.number().int(),
    explicitCacheReadTokens: z.number().int(),
    cacheWriteTokens: z.number().int(),
    /** Keyed by seconds, `0` when the provider didn't say how long it keeps them. */
    cacheWriteTokensByTtlSeconds: z.record(z.string(), z.number().int()),
    inputTokensByModality: z.record(z.string(), z.number().int()),
    outputTokens: z.number().int(),
    /** Null when the provider folded reasoning into output without saying how much. */
    reasoningTokens: z.number().int().nullable(),
    outputTokensByModality: z.record(z.string(), z.number().int()),
    clientToolCalls: z.number().int(),
    serverToolCalls: z.record(z.string(), z.number().int()),
    outputTextChars: z.number().int(),
    reasoningVisibility: z.enum(["none", "summary", "encrypted"]).nullable(),
    /** One line per meter the call used, priced at the `sku` row that holds. */
    costLines: z.array(CostLine),
    unknownCostLines: z.number().int(),
    unpricedCostLines: z.number().int(),
    /** The sum of `costLines`, or null when a line is unknown or unpriced. */
    listCostUsd: z.number().nullable(),
    contractedCostUsd: z.number().nullable(),
    /** What a seller reported it billed; null when none did. */
    billedCostUsd: z.number().nullable(),
    bills: z.array(
      z.object({
        seller: z.string(),
        billingAccount: z.string(),
        billedCostUsd: z.number(),
        source: z.string(),
      }),
    ),
    status: z.enum(["ok", "failed", "cancelled", "violated"]),
    upstreamContacted: z.boolean(),
    httpStatus: z.number().int(),
    finishReasons: z.array(z.string()),
    nativeFinishReasons: z.array(z.string()),
    /** Pax's name for what went wrong, such as `overloaded` or, for a violation, `invalid_json`. */
    errorType: Nullable,
    /** Where it came from: `network`, `gateway`, `provider`, or `pax` for a violation. */
    errorOrigin: Nullable,
    errorCode: Nullable,
    errorMessage: Nullable,
    errorRulesVersion: z.number().int(),
    accounting: z.object({
      payers: z.array(
        z.object({ payerId: z.uuid(), weight: z.number().int(), chargedRawCredits: z.string() }),
      ),
      reservedRawCredits: z.string(),
      chargedRawCredits: z.string(),
      settlement: Nullable,
      settlementReason: Nullable,
    }),
    prediction: z.object({
      predictorVersion: z.string(),
      /** What the cost figures rest on: `rowsTimesPredictedUsage`, `effectivePrices` or `none`. */
      basis: z.string(),
      samples: z.number().int(),
      upstreamRejection: z.boolean(),
      /** Predicted tokens and timing. */
      estimates: z.array(
        z.object({ target: z.string(), percentile: z.number().int(), value: z.number() }),
      ),
      costLines: z.array(CostLine.extend({ percentile: z.number().int() })),
      p50CostUsd: z.number().nullable(),
      p99CostUsd: z.number().nullable(),
      ceilingCostUsd: z.number().nullable(),
      ceilingSource: Nullable,
      /** Which figure the hold reserved: `p99`, `ceiling` or `nothing`, a hold of zero. */
      reservedFrom: Nullable,
    }),
  })
  .meta({ id: "AiCall", description: "One stored call, without its request and response bodies." });
export type AiCall = z.infer<typeof AiCall>;

export const ListAiCallsResponse = z
  .object({
    items: z.array(AiCall).meta({ description: "Newest first." }),
    nextCursor: z.string().nullable(),
  })
  .meta({
    id: "ListAiCallsResponse",
    description: "One page of stored calls.",
    example: { items: [], nextCursor: null },
  });
export type ListAiCallsResponse = z.infer<typeof ListAiCallsResponse>;

/** A call with the exact request and response bodies the provider saw and sent. */
export const AiCallDetail = AiCall.extend({
  /** The URL the caller sent the call to. */
  requestUrl: z.string(),
  /** The caller's headers; only known-safe headers keep their values. */
  requestHeaders: z.record(z.string(), z.string()),
  /** The vendor URL Pax forwarded the call to. */
  upstreamUrl: z.string(),
  /** The headers Pax sent upstream; only known-safe headers keep their values. */
  upstreamRequestHeaders: z.record(z.string(), z.string()),
  requestBody: z.string(),
  responseBody: z.string(),
  /** When each streamed event arrived, in milliseconds after `receivedAt`, in body order. */
  eventOffsetsMs: z.array(z.number().int()),
  /** The earlier call under the same cache key whose prompt shares the most with this one's. */
  cacheSource: z
    .object({
      callId: AiCallId,
      endedAt: z.string(),
      billedBy: z.string(),
      servedUpstream: Nullable,
      /** How many of this call's prompt parts, from the first, the source's prompt starts with. */
      sharedParts: z.number().int(),
    })
    .nullable(),
  /**
   * Earlier calls whose whole prompt is this prompt's first `promptParts` parts, with the
   * reasoning tokens each produced, for pricing the encrypted reasoning that follows them.
   */
  earlierReasoning: z.array(
    z.object({ promptParts: z.number().int(), reasoningTokens: z.number().int() }),
  ),
}).meta({ id: "AiCallDetail", description: "One stored call with both of its bodies." });
export type AiCallDetail = z.infer<typeof AiCallDetail>;
